import { Link, router, useForm, usePage } from "@inertiajs/react"
import { Globe, PlusIcon } from "lucide-react"
import { type ReactNode, useState, useEffect } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AdminLayout } from "@/layouts/AdminLayout"
import type {
  PaginationMeta,
  RegionOption,
  RegionRow,
} from "@/lib/types"
import type { SharedProps } from "@/types"

interface Filters {
  root_only?: string
  has_distributor?: string
  has_boundary?: string
}

interface Props {
  regions: RegionRow[]
  meta: PaginationMeta
  filters: Filters
  all_regions: RegionOption[]
  urls: { index: string; create: string }
}

function FilterBar({ filters, indexUrl }: { filters: Filters; indexUrl: string }) {
  function update(key: keyof Filters, value: string | undefined) {
    const next = { ...filters, [key]: value || undefined }
    router.get(indexUrl, next, { preserveState: true, replace: true })
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        variant={filters.root_only === "true" ? "secondary" : "outline"}
        size="sm"
        onClick={() => update("root_only", filters.root_only === "true" ? undefined : "true")}
      >
        Root regions only
      </Button>

      <Select
        value={filters.has_distributor ?? "any"}
        onValueChange={(v) => update("has_distributor", v === "any" ? undefined : v)}
      >
        <SelectTrigger className="h-8 w-44 text-sm">
          <SelectValue placeholder="Has distributor" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any</SelectItem>
          <SelectItem value="true">Has distributor</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.has_boundary ?? "any"}
        onValueChange={(v) => update("has_boundary", v === "any" ? undefined : v)}
      >
        <SelectTrigger className="h-8 w-40 text-sm">
          <SelectValue placeholder="Has boundary" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any</SelectItem>
          <SelectItem value="true">Has boundary</SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}

interface NewRegionFormProps {
  allRegions: RegionOption[]
  createUrl: string
  onClose: () => void
}

function NewRegionForm({ allRegions, createUrl, onClose }: NewRegionFormProps) {
  const { data, setData, post, processing, errors } = useForm({
    name: "",
    slug: "",
    parent_id: "",
    timezone: "",
    locale: "",
    description: "",
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post(createUrl, { onSuccess: onClose })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
      <div className="grid gap-1.5">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={data.name}
          onChange={(e) => setData("name", e.target.value)}
          required
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          value={data.slug}
          onChange={(e) => setData("slug", e.target.value)}
          required
        />
        {errors.slug && <p className="text-xs text-destructive">{errors.slug}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="parent_id">Parent region</Label>
        <Select
          value={data.parent_id || "none"}
          onValueChange={(v) => setData("parent_id", v === "none" ? "" : v)}
        >
          <SelectTrigger id="parent_id">
            <SelectValue placeholder="None (root region)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None (root region)</SelectItem>
            {allRegions.map((r) => (
              <SelectItem key={r.id} value={String(r.id)}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="timezone">Timezone</Label>
        <Input
          id="timezone"
          value={data.timezone}
          onChange={(e) => setData("timezone", e.target.value)}
          placeholder="e.g. Europe/Lisbon"
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="locale">Locale</Label>
        <Input
          id="locale"
          value={data.locale}
          onChange={(e) => setData("locale", e.target.value)}
          placeholder="e.g. pt"
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={processing}>
          {processing ? "Creating…" : "Create region"}
        </Button>
      </div>
    </form>
  )
}

function PaginationControls({ meta, indexUrl }: { meta: PaginationMeta; indexUrl: string }) {
  if (meta.pages <= 1) return null

  return (
    <div className="flex items-center justify-between py-3 text-sm text-muted-foreground">
      <span>
        {meta.count} region{meta.count !== 1 ? "s" : ""}
      </span>
      <div className="flex gap-2">
        {meta.page > 1 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => router.get(indexUrl, { page: meta.page - 1 }, { preserveState: true })}
          >
            Previous
          </Button>
        )}
        <span className="flex items-center px-2">
          Page {meta.page} of {meta.pages}
        </span>
        {meta.page < meta.pages && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => router.get(indexUrl, { page: meta.page + 1 }, { preserveState: true })}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  )
}

export default function RegionsIndex({ regions, meta, filters, all_regions, urls }: Props) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const [navigating, setNavigating] = useState(false)
  const { props } = usePage<SharedProps>()
  const isAdmin = props.user_context?.is_admin

  useEffect(() => {
    const off1 = router.on("start", () => setNavigating(true))
    const off2 = router.on("finish", () => setNavigating(false))
    return () => { off1(); off2() }
  }, [])

  const hasFilters = !!(filters.root_only || filters.has_distributor || filters.has_boundary)

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Regions</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Geographic hierarchy for properties, POIs, and distributors.
          </p>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setSheetOpen(true)}>
            <PlusIcon className="mr-1.5 size-4" />
            New region
          </Button>
        )}
      </div>

      <div className="mb-4">
        <FilterBar filters={filters} indexUrl={urls.index} />
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Parent</TableHead>
              <TableHead>Timezone</TableHead>
              <TableHead className="text-right">Properties</TableHead>
              <TableHead className="text-right">POIs</TableHead>
              <TableHead className="text-right">Distributors</TableHead>
              <TableHead>Boundary</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {navigating ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={8}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : regions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  {hasFilters ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">
                      No regions match your filters.
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <Globe className="mb-4 h-10 w-10 text-muted-foreground opacity-30" aria-hidden="true" />
                      <p className="text-sm font-medium text-muted-foreground">No regions yet.</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Create your first region to start organizing content.
                      </p>
                      {isAdmin && (
                        <Button size="sm" className="mt-4" onClick={() => setSheetOpen(true)}>
                          Create Region
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              regions.map((region) => (
                <TableRow key={region.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Link
                        href={region.url}
                        className="hover:underline"
                      >
                        {region.name}
                      </Link>
                      <span className="text-xs text-muted-foreground">
                        /{region.slug}
                      </span>
                      {region.at_risk && (
                        <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
                          At risk
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {region.parent_name ?? <span className="italic">—</span>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {region.timezone ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">{region.properties_count}</TableCell>
                  <TableCell className="text-right">{region.pois_count}</TableCell>
                  <TableCell className="text-right">{region.distributors_count}</TableCell>
                  <TableCell>
                    {region.has_boundary ? (
                      <Badge variant="default">Yes</Badge>
                    ) : (
                      <Badge variant="outline">No</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(region.updated_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationControls meta={meta} indexUrl={urls.index} />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>New region</SheetTitle>
          </SheetHeader>
          <NewRegionForm
            allRegions={all_regions}
            createUrl={urls.create}
            onClose={() => setSheetOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </div>
  )
}

RegionsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
